import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { Rgb } from '@/lib/ar';

import type { TryOnApi } from './api';
import type { WhiteBalance } from './kiosk';
import type { ArMode } from './schemas';

/**
 * What the try-on screen needs from outside it (BP-00 R5's shape).
 *
 * The camera and the clock are injected for the same reason the api is: the
 * screen's logic — which shade, how fast, when to offer photo mode — is
 * testable in jsdom, and the three things that are not (a `MediaStream`, a
 * WASM landmarker, `performance.now`) are the three things handed in.
 */
export interface TryOnDeps {
  api: TryOnApi;
  /** `phone` or, under `?mode=tablet`, `tablet` (T7). */
  mode: ArMode;
  /** Open the camera. Resolves to `null` when it could not be opened. */
  openCamera: () => Promise<MediaStream | null>;
  /** Shut it. Called on unmount, whatever else happened. */
  closeCamera: (stream: MediaStream | null) => void;
  /** Monotonic milliseconds. `performance.now` in a browser. */
  now: () => number;
  /** Where "book this look" goes. The app owns navigation, not the feature. */
  onBookThisLook: (skuId: string) => void;
  /**
   * T7: the middle of `frame`, in linear light, or `null` if it cannot be
   * read.
   *
   * Injected because sampling a pixel means a `<canvas>` and a `<video>`
   * that has decoded something, neither of which exists in jsdom — and
   * because what is worth testing is what the screen *does* with the three
   * numbers, not how they were read. The element is passed in rather than
   * found, so the sampler never has to know the screen's markup.
   */
  sampleGreyCard?: (frame: HTMLVideoElement) => Rgb | null;
  /** Where the gains go: the render loop corrects each frame with them. */
  onCalibrated?: (balance: WhiteBalance) => void;
  /**
   * BP-22: where a finished render's picture can be loaded from.
   *
   * Takes the render's **id**, not its `output_key`. The key is a storage
   * path, and a storage path on a client's screen is one somebody will
   * eventually try to edit. A function rather than a base URL because
   * where the api lives is the app's business, not the feature's.
   */
  outputUrl: (renderId: string) => string;
  /**
   * The OS share sheet, or `undefined` where the browser has none.
   *
   * `undefined` and not a no-op, which is what `lib/share` gives: a screen
   * can then hide the button instead of drawing one that does nothing.
   */
  shareImage?: (url: string, title: string) => Promise<void>;
}

const TryOnContext = createContext<TryOnDeps | null>(null);

export function TryOnProvider({
  deps,
  children,
}: {
  deps: TryOnDeps;
  children: ReactNode;
}): JSX.Element {
  return <TryOnContext.Provider value={deps}>{children}</TryOnContext.Provider>;
}

/** The dependencies. Throws when the screen is rendered outside the provider. */
export function useTryOnDeps(): TryOnDeps {
  const deps = useContext(TryOnContext);
  if (deps === null) {
    throw new Error('26AD try-on: this screen must be rendered inside <TryOnProvider>');
  }
  return deps;
}
